<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Actividades"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o descripción..."
    >
      <template #actions>
        <button
          class="btn btn-categorias btn-header"
          @click="abrirModalCategorias"
          title="Gestionar categorías"
        >
          <i class="bi bi-tags-fill"></i>
          <span class="btn-header-texto ms-2">Categorías</span>
        </button>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-plus-lg"></i>
          <span class="btn-header-texto ms-2">Nueva actividad</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="actividadesFiltradas"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="descripcion"
                label="Descripción"
                :filterable="false"
                :items="actividadesFiltradas"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="categoria_nombre"
                label="Categoría"
                :sortable="false"
                :items="actividadesParaFiltro"
                :modelValue="filtroCategoriaSet"
                @update:modelValue="filtroCategoriaSet = $event"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="act in actividadesPaginadas" :key="act.id">
            <td class="ps-4">
              <span class="fw-bold text-cafe">{{ act.nombre }}</span>
            </td>
            <td>
              <span class="descripcion-texto">{{ act.descripcion || "—" }}</span>
            </td>
            <td class="text-center">
              <span v-if="act.categoria" class="badge-categoria">{{
                nombreCategoria(act.categoria)
              }}</span>
              <span v-else class="text-muted-divisan">—</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(act)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarEliminar(act)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="actividadesPaginadas.length === 0">
            <td colspan="4" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de
            {{ actividadesOrdenadas.length }} actividades</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="act in actividadesPaginadas"
        :key="'mob-' + act.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-bold text-cafe fs-6">{{ act.nombre }}</span>
          <div class="d-flex gap-2">
            <button class="btn-circular-sm" @click="abrirModal(act)">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button class="btn-circular-sm" @click="confirmarEliminar(act)">
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
          <span v-if="act.categoria" class="badge-categoria">{{
            nombreCategoria(act.categoria)
          }}</span>
        </div>
        <small class="descripcion-texto">{{ act.descripcion || "—" }}</small>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ actividadesOrdenadas.length }}</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL CREAR / EDITAR -->
    <div class="modal fade" id="modalActividad" tabindex="-1" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar actividad" : "Nueva actividad" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarActividad">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  placeholder="Ej. Inspección visual de rodamientos"
                  required
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan">Descripción</label>
                <textarea
                  v-model="form.descripcion"
                  class="form-control divisan-input-modal"
                  rows="3"
                  placeholder="Describe el procedimiento o alcance..."
                ></textarea>
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Categoría <span class="text-muted-divisan">(opcional)</span></label
                >
                <select v-model="form.categoria" class="form-select divisan-input-modal">
                  <option :value="null">Sin categoría</option>
                  <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                    {{ cat.nombre }}
                  </option>
                </select>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-cancelar me-2" data-bs-dismiss="modal">
                Cancelar
              </button>
              <button type="submit" class="btn btn-nuevo px-4 py-2" :disabled="guardando">
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar" : "Guardar actividad" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════
         MODAL CATEGORÍAS
    ══════════════════════════════════════════════════════ -->
    <div
      class="modal fade"
      id="modalCategorias"
      tabindex="-1"
      ref="modalCategoriasElement"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              <i class="bi bi-tags-fill me-2"></i>Categorías de actividades
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <div class="modal-body pt-2">
            <!-- Formulario inline agregar / editar categoría -->
            <div class="cat-form-wrapper mb-4">
              <div class="row g-2 align-items-end">
                <div class="col-12 col-md-4">
                  <label class="form-label label-divisan mb-1"
                    >Nombre <span class="text-danger">*</span></label
                  >
                  <input
                    v-model="catForm.nombre"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Ej. Lubricación, Inspección..."
                    maxlength="100"
                  />
                </div>
                <div class="col-12 col-md-5">
                  <label class="form-label label-divisan mb-1"
                    >Descripción <span class="text-muted-divisan">(opcional)</span></label
                  >
                  <input
                    v-model="catForm.descripcion"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Descripción breve..."
                    maxlength="200"
                  />
                </div>
                <div class="col-12 col-md-3 d-flex gap-2">
                  <button
                    class="btn btn-nuevo flex-grow-1"
                    :disabled="!catForm.nombre.trim() || catGuardando"
                    @click="guardarCategoria"
                  >
                    <span
                      v-if="catGuardando"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    {{ catEditando ? "Actualizar" : "Agregar" }}
                  </button>
                  <button
                    v-if="catEditando"
                    class="btn btn-cancelar px-3"
                    @click="cancelarEdicionCat"
                    title="Cancelar edición"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Lista de categorías -->
            <div v-if="catLoading" class="text-center py-4">
              <div class="spinner-border text-verde"></div>
            </div>

            <div
              v-else-if="categorias.length === 0"
              class="text-center py-4 text-cafe opacity-50 fw-bold"
            >
              No hay categorías registradas
            </div>

            <div v-else class="cat-lista">
              <div
                v-for="cat in categorias"
                :key="cat.id"
                class="cat-item d-flex justify-content-between align-items-center px-3 py-2"
                :class="{ 'cat-editando': catForm.id === cat.id }"
              >
                <div class="d-flex flex-column">
                  <span class="fw-bold text-cafe">{{ cat.nombre }}</span>
                  <small v-if="cat.descripcion" class="text-muted-divisan">{{
                    cat.descripcion
                  }}</small>
                </div>
                <div class="d-flex gap-2">
                  <button
                    class="btn-circular-sm"
                    @click="editarCategoria(cat)"
                    title="Editar"
                  >
                    <i class="bi bi-pencil-square text-verde"></i>
                  </button>
                  <button
                    class="btn-circular-sm"
                    @click="confirmarDesactivarCat(cat)"
                    title="Desactivar"
                  >
                    <i class="bi bi-trash3-fill text-danger"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-cancelar px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getActividades,
  crearActividad,
  actualizarActividad,
  eliminarActividad,
  getCategoriasActividad,
  crearCategoriaActividad,
  actualizarCategoriaActividad,
  eliminarCategoriaActividad,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionActividadesStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionActividadesStore();

// ── Estado ──────────────────────────────────────────────────
const actividades = ref([]);
const categorias = ref([]);
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const filtro = ref("");
const filtroCategoriaSet = ref(new Set());
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

// ── Modal categorías ─────────────────────────────────────────
const modalCategoriasElement = ref(null);
let modalCategoriasInstance = null;
const catLoading = ref(false);
const catGuardando = ref(false);
const catEditando = ref(false);

const catFormVacio = () => ({ id: null, nombre: "", descripcion: "" });
const catForm = ref(catFormVacio());

function abrirModalCategorias() {
  cancelarEdicionCat();
  modalCategoriasInstance.show();
}

function editarCategoria(cat) {
  catEditando.value = true;
  catForm.value = { id: cat.id, nombre: cat.nombre, descripcion: cat.descripcion || "" };
}

function cancelarEdicionCat() {
  catEditando.value = false;
  catForm.value = catFormVacio();
}

async function guardarCategoria() {
  if (!catForm.value.nombre.trim()) return;
  catGuardando.value = true;
  try {
    const payload = {
      nombre: catForm.value.nombre.trim(),
      descripcion: catForm.value.descripcion.trim() || null,
    };
    if (catEditando.value) {
      await actualizarCategoriaActividad(catForm.value.id, payload);
    } else {
      await crearCategoriaActividad(payload);
    }
    cancelarEdicionCat();
    await cargarTodo();
    Swal.fire({
      icon: "success",
      title: catEditando.value ? "Categoría actualizada" : "Categoría creada",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch (err) {
    const msg =
      err?.response?.data?.nombre?.[0] ||
      err?.response?.data?.non_field_errors?.[0] ||
      "No se pudo guardar la categoría";
    Swal.fire("Error", msg, "error");
  } finally {
    catGuardando.value = false;
  }
}

async function confirmarDesactivarCat(cat) {
  const res = await Swal.fire({
    title: `¿Desactivar "${cat.nombre}"?`,
    text: "Las actividades que usan esta categoría la conservarán en su historial.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });
  if (!res.isConfirmed) return;
  try {
    await eliminarCategoriaActividad(cat.id);
    await cargarTodo();
    Swal.fire({
      icon: "success",
      title: "Desactivada",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo desactivar la categoría", "error");
  }
}

// ── Ordenamiento ────────────────────────────────────────────
const sortKey = ref("nombre");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

// ── Form ─────────────────────────────────────────────────────
const formVacio = () => ({ id: null, nombre: "", descripcion: "", categoria: null });
const form = ref(formVacio());

watch([filtro, filtroCategoriaSet], () => {
  paginaActual.value = 1;
});

// ── Computed ─────────────────────────────────────────────────
const actividadesBuscadas = computed(() => {
  const q = filtro.value.toLowerCase();
  return actividades.value.filter(
    (a) =>
      !q ||
      a.nombre.toLowerCase().includes(q) ||
      (a.descripcion || "").toLowerCase().includes(q)
  );
});

const actividadesParaFiltro = computed(() =>
  actividadesBuscadas.value.map((a) => ({
    ...a,
    categoria_nombre: nombreCategoria(a.categoria) || "Sin categoría",
  }))
);

const actividadesFiltradas = computed(() =>
  actividadesParaFiltro.value.filter(
    (a) =>
      !filtroCategoriaSet.value.size || filtroCategoriaSet.value.has(a.categoria_nombre)
  )
);

const actividadesOrdenadas = computed(() =>
  [...actividadesFiltradas.value].sort((a, b) => {
    let va = a[sortKey.value] ?? "";
    let vb = b[sortKey.value] ?? "";
    const cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(actividadesOrdenadas.value.length / porPagina.value))
);
const actividadesPaginadas = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return actividadesOrdenadas.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  actividadesOrdenadas.value.length === 0
    ? 0
    : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, actividadesOrdenadas.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

function nombreCategoria(id) {
  return categorias.value.find((c) => c.id === id)?.nombre ?? null;
}

// ── API ──────────────────────────────────────────────────────
async function cargarTodo() {
  loading.value = true;
  try {
    const [dataAct, dataCat] = await Promise.all([
      getActividades(),
      getCategoriasActividad(),
    ]);
    actividades.value = Array.isArray(dataAct) ? dataAct : dataAct.results ?? [];
    categorias.value = Array.isArray(dataCat) ? dataCat : dataCat.results ?? [];
  } catch {
    Swal.fire("Error", "No se pudieron cargar las actividades", "error");
  } finally {
    loading.value = false;
  }
}

// ── Modal ────────────────────────────────────────────────────
function abrirModal(act) {
  if (act) {
    editando.value = true;
    form.value = {
      id: act.id,
      nombre: act.nombre,
      descripcion: act.descripcion || "",
      categoria: act.categoria ?? null,
    };
  } else {
    editando.value = false;
    form.value = formVacio();
  }
  modalInstance.show();
}

async function guardarActividad() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre.trim(),
      descripcion: form.value.descripcion.trim(),
      categoria: form.value.categoria || null,
    };
    if (editando.value) {
      await actualizarActividad(form.value.id, payload);
    } else {
      await crearActividad(payload);
    }
    await cargarTodo();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Actividad ${editando.value ? "actualizada" : "creada"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "Ocurrió un problema al procesar la solicitud", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarEliminar(actividad) {
  const result = await Swal.fire({
    title: `¿Desactivar "${actividad.nombre}"?`,
    text: "La actividad no aparecerá en nuevas rutinas, pero el historial se conserva.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
  });
  if (result.isConfirmed) {
    try {
      await eliminarActividad(actividad.id);
      await cargarTodo();
      Swal.fire({
        icon: "success",
        title: "Desactivada",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar la actividad.", "error");
    }
  }
}

onMounted(() => {
  cargarTodo();
  modalInstance = new Modal(modalElement.value);
  modalCategoriasInstance = new Modal(modalCategoriasElement.value);
});
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-divisan {
  color: rgba(64, 34, 8, 0.6);
  font-weight: 500;
  font-size: 0.85rem;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-cancelar {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  font-weight: 600;
  border-radius: 8px;
  padding: 0.5rem 1rem;
}
.btn-cancelar:hover {
  background: rgba(96, 108, 56, 0.08);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 15px;
  font-weight: 800;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}

.descripcion-texto {
  color: var(--d-green);
  font-size: 0.88rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.badge-categoria {
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  border: 1px solid rgba(64, 34, 8, 0.15);
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
  cursor: pointer;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.8rem;
}
.btn-circular-sm:hover {
  background: rgba(96, 108, 56, 0.1);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
}
.divisan-input-modal:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(96, 108, 56, 0.2);
}

@media (max-width: 991.98px) {
  :deep(.modal) {
    z-index: 1200;
  }
  :deep(.modal-backdrop) {
    z-index: 1199;
  }
  :deep(.modal-dialog) {
    margin-top: 75px;
  }
}
@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

.btn-categorias {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-categorias:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}

/* ── Lista de categorías dentro del modal ── */
.cat-form-wrapper {
  background: rgba(96, 108, 56, 0.05);
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 12px;
  padding: 1rem;
}
.cat-lista {
  border: 1px solid rgba(96, 108, 56, 0.15);
  border-radius: 12px;
  overflow: hidden;
}
.cat-item {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  background: var(--d-cream);
  transition: background 0.15s;
}
.cat-item:last-child {
  border-bottom: none;
}
.cat-item:hover {
  background: rgba(96, 108, 56, 0.05);
}
.cat-item.cat-editando {
  background: rgba(64, 34, 8, 0.05);
  border-left: 4px solid var(--d-brown);
}
</style>
